// HOW AN IDENTITY CHECK WAS MADE (26/10/2026, verify from documents).
//
// A Sumsub verification row is one of three kinds, told apart by the reference
// the platform gave Sumsub (sumsub_verifications.external_user_id, written once
// when the check is created, never by a client):
//   link          the person was sent a secure link and completed the check
//                 themselves (document, selfie and liveness);
//   documents     the officer sent the passport and proof of address the firm
//                 holds (filed by Manual Upload); no selfie and no liveness, and
//                 the person was not contacted;
//   screen_only   AML screening only, from the name and details on file.
// One source for the dashboard, the Audit Record and the server.

// The documents mark ends in an underscore, which no name slug can produce (review,
// 26/10/2026: a person called "Docs ..." would otherwise read as a check from documents).
export const DOCS_MARK = 'docs_';
export const SCREEN_ONLY_MARK = 'aml';

const slug = (name) => String(name || '').replace(/\s+/g, '-').replace(/[^a-zA-Z0-9-]/g, '').toLowerCase();
export const markedExternalUserId = (sessionId, mark, personName, now = Date.now()) => `${sessionId}-${mark}${mark.endsWith('_') ? '' : '-'}${slug(personName)}-${now}`;

export function verificationMethod(v) {
  const ref = String((v && (v.external_user_id || v.externalUserId)) || '');
  const sid = String((v && v.session_id) || '');
  const rest = sid && ref.startsWith(sid + '-') ? ref.slice(sid.length + 1) : ref.replace(/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}-/i, '');
  if (rest.startsWith(DOCS_MARK)) return 'documents';
  if (rest.startsWith(SCREEN_ONLY_MARK + '-')) return 'screen_only';
  return 'link';
}

// WHAT A RESULT COUNTS AS FOR THE PERSON'S IDENTITY (owner, 29/10/2026). A
// clear Screen only result is a screen, never an identity verification: on a
// new client the person still needs a link or Verify from documents before
// sign off, and the gate says so. A migrated case keeps its behaviour: there
// the identity rests on the firm's prior records (the Screen only note says
// so), and a clear screen completes the person. One rule for the identity set,
// the case state, the sign off gate and the panel.
export const SCREENED_ONLY = 'screened_only';
export function identityStatusOf(v, { origin } = {}) {
  const st = String((v && v.status) || '');
  if (v && verificationMethod(v) === 'screen_only' && (st === 'approved' || st === 'approved_with_flags') && origin !== 'migrated') return SCREENED_ONLY;
  return st;
}
// Which of a person's rows stands for them (one per person on the panel, the
// case state and the gate): by status, except that on a new client a Screen
// only row ranks below every identity row, so a link or a check from documents
// (pending, withdrawn or done) is the one shown and counted.
// pending (a check from documents or a screen under way) ranks with in_progress, so a
// new check after a withdrawn one is shown. withdrawn ranks with link_generated (the
// newer wins): v1_811, a withdrawal stands over an older link row after a reload.
const STATUS_RANK = { approved: 5, approved_with_flags: 4, requires_review: 3, rejected: 3, in_progress: 2, pending: 2, link_generated: 1, withdrawn: 1 };
export function identityRank(v, { origin } = {}) {
  const base = STATUS_RANK[String((v && v.status) || '').toLowerCase()] || 0;
  return (v && verificationMethod(v) === 'screen_only' && origin !== 'migrated') ? Math.min(base, 0.5) : base;
}
export const SCREENED_ONLY_WORDS = 'screened only, identity not verified: send a link or use Verify from documents';

// The words the officer and the Audit Record read (no dashes).
export const METHOD_WORDS = Object.freeze({
  link: 'Secure link completed by the person (identity document, selfie and liveness)',
  documents: 'From documents filed by the firm (passport and proof of address received as scans), without a selfie or liveness check; the person was not contacted',
  screen_only: 'AML screening only, from the details on file, without identity verification; the person was not contacted',
});

// FINMA Circular 2016/7 (online identification): what the establishing person's
// panel says when that person's check was made from documents (review, 26/10/2026:
// the standing text said liveness was covered, which is false for this check).
export const ONLINE_ID_DOCS_WORDS = 'Document authenticity and proof of address are covered by the Sumsub check above, which was made from documents filed by the firm, without a selfie or liveness check. Online identification under FINMA Circular 2016/7 is therefore not covered by this check: record how the establishing person was identified.';

// The files Sumsub reads for a check from documents: images or PDF.
export const DOCS_ACCEPT_EXT = Object.freeze(['jpg', 'jpeg', 'png', 'pdf', 'heic']);
export const DOCS_MAX_BYTES = 50 * 1024 * 1024;
const extOf = (name) => (String(name || '').toLowerCase().match(/\.([a-z0-9]+)$/) || [])[1] || '';

// A document the officer may pick: filed by Manual Upload on this case, with a
// stored file Sumsub can read.
export function docsEligible(doc) {
  if (!doc || !doc.file_path) return false;
  if (doc.supplied_via !== 'officer_upload') return false;
  return DOCS_ACCEPT_EXT.includes(extOf(doc.file_name || doc.file_path));
}

// Proof of address kinds Sumsub accepts, as the officer picks them.
export const ADDRESS_DOC_TYPES = Object.freeze([
  ['UTILITY_BILL', 'Utility bill'],
  ['BANK_STATEMENT', 'Bank statement'],
  ['OTHER', 'Other proof of address'],
]);
